/* eslint-disable @angular-eslint/prefer-inject, @angular-eslint/prefer-standalone -- System settings retains its existing NgModule form contract. */
import {Component, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {MatSnackBar}            from '@angular/material/snack-bar';
import {SettingsService}        from '@src/app/core/services/settings.service';
import {BillingPolicyService}   from '@src/app/core/services/billing-policy.service';
import {ApiService}             from '@src/app/shared/services/api.service';
import {Services} from '@src/app/core/enums/services.enum';

@Component({
    standalone: false,
    selector: 'app-system-settings',
    templateUrl: './system-settings.component.html',
})
export class SystemSettingsComponent
    implements OnInit
{
    public readonly services = [{id: Services.FireStopping, label: 'Fire stopping'}, {id: Services.FireDoors, label: 'Fire doors'}];
    public savingServices = false;
    public servicesSubmitted = false;
    public servicesError = '';
    public systemSettingsForm: FormGroup = new FormGroup({
        tenant: new FormGroup({
            timezone: new FormControl('')
        }),
        credits: new FormGroup({
            low_credit_warning: new FormControl<number>(0)
        }),
        services: new FormGroup({
            enabled_services: new FormControl<string[]>([], {nonNullable: true, validators: Validators.required})
        }),
        pricing: new FormGroup({
            pricing_enabled: new FormControl<boolean>(true)
        }),
    });

    get form()
    {
        return this.systemSettingsForm.controls;
    }

    constructor(
        private apiService: ApiService,
        private snackBar: MatSnackBar,
        private settingsService: SettingsService,
        public billingPolicies: BillingPolicyService,
    )
    {
    }

    public ngOnInit(): void
    {
        this.loadSettings();
    }

    public toggleService(id: string, checked: boolean): void
    {
        const control = this.form.services.get('enabled_services');
        control.setValue(checked ? [...control.value, id] : control.value.filter(value => value !== id));
    }

    public submitServices(): void
    {
        this.servicesSubmitted = true;
        if (this.savingServices || this.form.services.invalid) { return; }
        this.servicesError = '';
        this.savingServices = true;
        this.apiService.updateSettings('settings/services', this.form.services.value).subscribe({
            next: response => {
                this.settingsService.hydrate(response.data);
                this.savingServices = false;
                this.snackBar.open('Services updated.', 'Ok', {duration: 2500});
            },
            error: () => {
                this.savingServices = false;
                this.servicesError = 'We couldn’t save your services. Please try again.';
            },
        });
    }

    public submitCredits()
    {
        if (this.form.credits.valid) {
            this.apiService.updateSettings('settings/credits', this.form.credits.value).subscribe({
                next: response => {
                    this.form.credits.get('low_credit_warning').setValue(response.data.find(c => c.name == 'low_credit_warning').value);
                    this.snackBar.open('You have successfully updated the credits settings.', 'Ok', {duration: 2500});
                },
                error: error => this.apiService.handleError(error)
            });
        } else {
            this.snackBar.open('Please ensure all inputs for the credits section are filled out correctly.', 'Ok', {duration: 2500});
        }
    }

    public submitPricing(): void
    {
        if (this.form.pricing.valid) {
            this.apiService.updateSettings('settings/pricing', this.form.pricing.value).subscribe({
                next: response => {
                    const pricingEnabled = !!response.data.find(setting => setting.name == 'pricing_enabled')?.value;
                    this.form.pricing.get('pricing_enabled').setValue(pricingEnabled);
                    this.settingsService.setPricingEnabled(pricingEnabled);
                    this.snackBar.open('You have successfully updated the pricing settings.', 'Ok', {duration: 2500});
                },
                error: error => this.apiService.handleError(error)
            });
        } else {
            this.snackBar.open('Please ensure the pricing section is filled out correctly.', 'Ok', {duration: 2500});
        }
    }

    private loadSettings(): void
    {
        this.apiService.list('settings').subscribe({
            next: response => {
                this.form.credits.get('low_credit_warning').setValue(response.data.find(setting => setting.name == 'low_credit_warning')?.value ?? 0);
                this.settingsService.hydrate(response.data);
                this.form.services.get('enabled_services').setValue(this.settingsService.enabledServices$.value);
                this.form.pricing.get('pricing_enabled').setValue(this.settingsService.isPricingEnabled());
            },
            error: error => this.apiService.handleError(error)
        });
    }
}
